import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical, Plus, X } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import JobPublishPanel from '../components/JobPublishPanel'
import FilesTab from '../components/FilesTab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Job = {
  id: number
  code: string
  title: string
  description: string
  requirements: string
  responsibilities: string
  status: 'DRAFT' | 'APPROVED' | 'OPEN' | 'PAUSED' | 'CLOSED' | 'CANCELLED'
  employment_type: 'FULL_TIME' | 'PART_TIME' | 'CONTRACTOR' | 'INTERN'
  location_type: 'ONSITE' | 'REMOTE' | 'HYBRID'
  location_country: string
  location_city: string
  department: number | null
  department_name: string | null
  position: number | null
  position_title: string | null
  hiring_manager: number | null
  hiring_manager_name: string | null
  recruiter: number | null
  recruiter_name: string | null
  pipeline: number | null
  pipeline_name: string | null
  tags: number[]
  tags_detail: Tag[]
  openings: number
  salary_min: string | null
  salary_max: string | null
  salary_currency_code: string
  filled_count: number
  remaining_openings: number
  application_count: number
  notes: string
}

type Department = { id: number; code: string; name: string; is_active: boolean }
type PersonLite = { id: number; full_name: string; personal_email?: string }
type PipelineLite = {
  id: number; name: string; is_default: boolean; is_active: boolean; stage_count: number
}
type Tag = { id: number; name: string; category: string; color: string }

type AppCard = {
  id: number
  person_id: number
  person_name: string
  person_email: string
  source: string
  applied_at: string
  resume_url: string
  status: 'ACTIVE' | 'HIRED' | 'REJECTED' | 'WITHDRAWN'
}

type KanbanStage = {
  id: number
  name: string
  order: number
  color: string
  is_won: boolean
  is_lost: boolean
  applications: AppCard[]
}

type KanbanResponse = {
  job: {
    id: number; code: string; title: string; status: Job['status']
    pipeline_id: number | null; pipeline_name: string | null
    openings: number; filled_count: number; remaining_openings: number
  }
  stages: KanbanStage[]
}

const STATUS_COLOR: Record<Job['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  OPEN: 'bg-green-100 text-green-800',
  PAUSED: 'bg-amber-100 text-amber-700',
  CLOSED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const EMPLOYMENT_LABEL: Record<Job['employment_type'], string> = {
  FULL_TIME: 'Full-time', PART_TIME: 'Part-time',
  CONTRACTOR: 'Contractor', INTERN: 'Intern',
}

const LOCATION_LABEL: Record<Job['location_type'], string> = {
  ONSITE: 'On-site', REMOTE: 'Remote', HYBRID: 'Hybrid',
}

// Map stage colour names to Tailwind classes. Falls back to slate.
const STAGE_HEADER_COLOR: Record<string, string> = {
  slate: 'bg-slate-100 text-slate-700 border-slate-300',
  gray: 'bg-gray-100 text-gray-700 border-gray-300',
  blue: 'bg-blue-100 text-blue-700 border-blue-300',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  violet: 'bg-violet-100 text-violet-700 border-violet-300',
  amber: 'bg-amber-100 text-amber-700 border-amber-300',
  green: 'bg-green-100 text-green-800 border-green-300',
  red: 'bg-red-100 text-red-700 border-red-300',
}

type TabKey = 'details' | 'careers' | 'pipeline' | 'applications' | 'files'

// ─────────────────── Page ───────────────────
export default function ATSJobDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const jobId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const jobQ = useQuery({
    queryKey: ['ats', 'job', jobId],
    queryFn: async () => (await api.get<Job>(`/ats/jobs/${jobId}/`)).data,
    enabled: !!jobId,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<PersonLite[]>('/hr/persons/')).data,
  })
  const pipelinesQ = useQuery({
    queryKey: ['ats', 'pipelines'],
    queryFn: async () => (await api.get<PipelineLite[]>('/ats/pipelines/')).data,
  })
  const tagsQ = useQuery({
    queryKey: ['ats', 'tags'],
    queryFn: async () => (await api.get<Tag[]>('/ats/tags/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Job>) =>
      (await api.patch(`/ats/jobs/${jobId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'job', jobId] })
      qc.invalidateQueries({ queryKey: ['ats', 'jobs'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/ats/jobs/${jobId}/`),
    onSuccess: () => {
      toast.success('Job deleted')
      qc.invalidateQueries({ queryKey: ['ats', 'jobs'] })
      navigate('/admin/ats/jobs')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (jobQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (jobQ.isError || !jobQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Job not found.</p>
          <Link to="/admin/ats/jobs" className="text-blue-600 hover:underline">← Back to jobs</Link>
        </div>
      </AdminLayout>
    )
  }

  const job = jobQ.data
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)
  const persons = personsQ.data || []
  const pipelines = (pipelinesQ.data || []).filter((p) => p.is_active)
  const allTags = tagsQ.data || []
  const saveJob = (payload: Partial<Job>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/ats/jobs" className="text-muted-foreground hover:text-foreground hover:underline">← Jobs</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{job.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={job.title} onSave={(v) => saveJob({ title: v })} />
            </h2>
            <div className="text-sm text-muted-foreground mt-0.5">
              {job.department_name && <span>{job.department_name} · </span>}
              {EMPLOYMENT_LABEL[job.employment_type]} · {LOCATION_LABEL[job.location_type]}
              {job.location_country && <span> · {job.location_country}</span>}
              {' · '}
              <span>{job.filled_count}/{job.openings} filled</span>
              {' · '}
              <span>{job.application_count} applications</span>
            </div>
            <div className="mt-2">
              <JobTagsEditor
                allTags={allTags}
                value={job.tags_detail || []}
                onChange={(ids) => saveJob({ tags: ids })}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={job.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'OPEN', label: 'Open' },
                { value: 'PAUSED', label: 'Paused' },
                { value: 'CLOSED', label: 'Closed' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
              onSave={(v) => saveJob({ status: v as Job['status'] })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`job ${job.code}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete job</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'careers', 'pipeline', 'applications', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'careers' && 'Careers page'}
              {t === 'pipeline' && `Pipeline${job.pipeline_name ? ` (${job.pipeline_name})` : ''}`}
              {t === 'applications' && `Applications (${job.application_count})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab job={job} departments={departments} persons={persons} pipelines={pipelines} onSave={saveJob} />
      )}
      {tab === 'careers' && (
        <JobPublishPanel job={job as any} onChanged={() => jobQ.refetch()} />
      )}
      {tab === 'pipeline' && <PipelineTab jobId={job.id} persons={persons} />}
      {tab === 'applications' && <ApplicationsTab jobId={job.id} persons={persons} />}
      {tab === 'files' && <FilesTab parent="ats.job" parentId={jobId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  job, departments, persons, pipelines, onSave,
}: {
  job: Job
  departments: Department[]
  persons: PersonLite[]
  pipelines: PipelineLite[]
  onSave: (payload: Partial<Job>) => Promise<void>
}) {
  const pipelineLocked = job.application_count > 0
  const [migrateOpen, setMigrateOpen] = useState(false)
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Role">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Title">
            <InlineText value={job.title} onSave={(v) => onSave({ title: v })} />
          </Row>
          <Row label="Department">
            <InlineSelect
              value={job.department ? String(job.department) : ''}
              displayLabel={job.department_name || <Dash />}
              allowClear
              options={departments.map((d) => ({
                value: String(d.id), label: `${d.code} ${d.name}`, searchText: `${d.code} ${d.name}`,
              }))}
              onSave={(v) => onSave({ department: v ? Number(v) : null })}
            />
          </Row>
          <Row label="Employment type">
            <InlineSelect
              value={job.employment_type}
              options={(Object.keys(EMPLOYMENT_LABEL) as Job['employment_type'][]).map((k) => ({
                value: k, label: EMPLOYMENT_LABEL[k],
              }))}
              onSave={(v) => onSave({ employment_type: v as Job['employment_type'] })}
            />
          </Row>
          <Row label="Location type">
            <InlineSelect
              value={job.location_type}
              options={(Object.keys(LOCATION_LABEL) as Job['location_type'][]).map((k) => ({
                value: k, label: LOCATION_LABEL[k],
              }))}
              onSave={(v) => onSave({ location_type: v as Job['location_type'] })}
            />
          </Row>
          <Row label="Country (ISO 2)">
            <InlineText
              value={job.location_country}
              onSave={(v) => onSave({ location_country: v.toUpperCase().slice(0, 2) })}
              placeholder="RO, DE, US, …"
            />
          </Row>
          <Row label="City">
            <InlineText
              value={job.location_city}
              onSave={(v) => onSave({ location_city: v })}
            />
          </Row>
        </dl>
      </Card>

      <Card title="Team & headcount">
        <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
          <Row label="Hiring manager">
            <InlineSelect
              value={job.hiring_manager ? String(job.hiring_manager) : ''}
              displayLabel={job.hiring_manager_name || <Dash />}
              allowClear
              options={persons.map((p) => ({
                value: String(p.id), label: p.full_name, searchText: p.full_name,
              }))}
              onSave={(v) => onSave({ hiring_manager: v ? Number(v) : null })}
            />
          </Row>
          <Row label="Recruiter">
            <InlineSelect
              value={job.recruiter ? String(job.recruiter) : ''}
              displayLabel={job.recruiter_name || <Dash />}
              allowClear
              options={persons.map((p) => ({
                value: String(p.id), label: p.full_name, searchText: p.full_name,
              }))}
              onSave={(v) => onSave({ recruiter: v ? Number(v) : null })}
            />
          </Row>
          <Row label="Openings">
            <InlineNumber
              value={String(job.openings)}
              onSave={(v) => onSave({ openings: Math.max(1, Number(v) || 1) })}
            />
          </Row>
          <Row label="Hiring pipeline">
            {pipelineLocked ? (
              <div className="text-sm">
                <span className="font-medium">{job.pipeline_name || <Dash />}</span>
                <div className="text-xs text-muted-foreground mt-0.5">
                  🔒 Locked — {job.application_count} application
                  {job.application_count === 1 ? '' : 's'} in flight. Switching pipelines
                  would orphan their stages.{' '}
                  <button
                    type="button"
                    className="text-violet-600 hover:underline"
                    onClick={() => setMigrateOpen(true)}
                  >
                    Migrate to another pipeline…
                  </button>
                </div>
                <MigratePipelineDrawer
                  open={migrateOpen}
                  onOpenChange={setMigrateOpen}
                  job={job}
                  pipelines={pipelines}
                />
              </div>
            ) : (
              <InlineSelect
                value={job.pipeline ? String(job.pipeline) : ''}
                displayLabel={job.pipeline_name || <Dash />}
                allowClear
                options={pipelines.map((p) => {
                  const hint = `${p.stage_count} stage${p.stage_count === 1 ? '' : 's'}${p.is_default ? ' · default' : ''}`
                  return {
                    value: String(p.id),
                    label: (
                      <span>
                        {p.name}
                        <span className="text-xs text-muted-foreground ml-2">{hint}</span>
                      </span>
                    ),
                    searchText: p.name,
                  }
                })}
                onSave={(v) => onSave({ pipeline: v ? Number(v) : null })}
                helper="Set when no candidates have applied yet. Empty = use the organisation default at next save."
              />
            )}
          </Row>
          <Row label="Filled / remaining">
            <span className="text-sm">
              {job.filled_count} filled · {job.remaining_openings} remaining
            </span>
          </Row>
          <Row label="Salary min">
            <InlineNumber
              value={job.salary_min || ''}
              onSave={(v) => onSave({ salary_min: v === '' ? null : v })}
              placeholder="Optional"
            />
          </Row>
          <Row label="Salary max">
            <InlineNumber
              value={job.salary_max || ''}
              onSave={(v) => onSave({ salary_max: v === '' ? null : v })}
              placeholder="Optional"
            />
          </Row>
          <Row label="Currency">
            <InlineText
              value={job.salary_currency_code}
              onSave={(v) => onSave({ salary_currency_code: v.toUpperCase().slice(0, 3) })}
            />
          </Row>
        </dl>
      </Card>

      <Card title="Description" className="lg:col-span-2">
        <InlineText
          value={job.description}
          multiline
          onSave={(v) => onSave({ description: v })}
          placeholder="What the role is, who it's for, why it exists."
        />
      </Card>

      <Card title="Requirements" className="lg:col-span-2">
        <InlineText
          value={job.requirements}
          multiline
          onSave={(v) => onSave({ requirements: v })}
          placeholder="Must-haves: years, skills, certifications, languages."
        />
      </Card>

      <Card title="Responsibilities" className="lg:col-span-2">
        <InlineText
          value={job.responsibilities}
          multiline
          onSave={(v) => onSave({ responsibilities: v })}
          placeholder="What this hire will own day-to-day."
        />
      </Card>

      <Card title="Internal notes" className="lg:col-span-2">
        <InlineText
          value={job.notes}
          multiline
          onSave={(v) => onSave({ notes: v })}
          placeholder="Recruiter scratch pad — context for the hiring panel."
        />
      </Card>
    </div>
  )
}

// ─────────────────── Pipeline tab (Kanban) ───────────────────

function PipelineTab({ jobId, persons }: { jobId: number; persons: PersonLite[] }) {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const kanbanQ = useQuery({
    queryKey: ['ats', 'kanban', jobId],
    queryFn: async () => (await api.get<KanbanResponse>(`/ats/kanban/?job=${jobId}`)).data,
  })

  const moveMut = useMutation({
    mutationFn: async ({ appId, stageId }: { appId: number; stageId: number }) =>
      (await api.post(`/ats/applications/${appId}/move/`, { stage: stageId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ats', 'kanban', jobId] })
      qc.invalidateQueries({ queryKey: ['ats', 'job', jobId] })
    },
    onError: (e) => {
      toast.error(extractErr(e))
      qc.invalidateQueries({ queryKey: ['ats', 'kanban', jobId] })
    },
  })

  // Drag-drop state — track the application being dragged so the drop target
  // can call move with the right id.
  const [dragId, setDragId] = useState<number | null>(null)
  const [dragOverStage, setDragOverStage] = useState<number | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  if (kanbanQ.isLoading) return <p className="text-muted-foreground">Loading…</p>
  if (kanbanQ.isError || !kanbanQ.data) {
    return <p className="text-sm text-red-700">Failed to load pipeline. Make sure the Job has a pipeline assigned.</p>
  }

  const { stages } = kanbanQ.data

  function onDrop(stageId: number) {
    if (dragId == null) return
    // Optimistic: just trigger mutation; react-query invalidate refetches.
    moveMut.mutate({ appId: dragId, stageId })
    setDragId(null)
    setDragOverStage(null)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Drag cards between stages to update an application's status. Terminal stages auto-flip the application to Hired / Rejected.
        </p>
        <Button onClick={() => setAddOpen(true)}><Plus className="h-3 w-3 mr-1" /> Add candidate</Button>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const colorCls = STAGE_HEADER_COLOR[stage.color] || STAGE_HEADER_COLOR.slate
          const isOver = dragOverStage === stage.id
          return (
            <div
              key={stage.id}
              className={`flex-shrink-0 w-64 bg-muted/30 rounded-lg border ${isOver ? 'border-blue-500 ring-2 ring-blue-200' : 'border-border'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id) }}
              onDragLeave={() => setDragOverStage((s) => s === stage.id ? null : s)}
              onDrop={() => onDrop(stage.id)}
            >
              <div className={`px-3 py-2 rounded-t-lg border-b text-xs font-semibold ${colorCls} flex items-center justify-between`}>
                <span>{stage.name}</span>
                <span className="text-xs opacity-70">
                  {stage.applications.length}
                  {stage.is_won && ' · won'}
                  {stage.is_lost && ' · lost'}
                </span>
              </div>
              <div className="p-2 space-y-2 min-h-[100px]">
                {stage.applications.map((a) => (
                  <div
                    key={a.id}
                    draggable
                    onDragStart={() => setDragId(a.id)}
                    onDragEnd={() => { setDragId(null); setDragOverStage(null) }}
                    onClick={() => navigate(`/admin/ats/applications/${a.id}`)}
                    className={`bg-card rounded border border-border p-2 text-xs cursor-grab active:cursor-grabbing hover:border-foreground/30 ${dragId === a.id ? 'opacity-50' : ''}`}
                  >
                    <div className="font-medium text-foreground">{a.person_name}</div>
                    {a.person_email && (
                      <div className="text-muted-foreground truncate">{a.person_email}</div>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      {a.source && <span className="text-muted-foreground">{a.source}</span>}
                      {a.resume_url && (
                        <a href={a.resume_url} target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}>
                          CV
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {stage.applications.length === 0 && (
                  <div className="text-xs text-muted-foreground italic text-center py-4">
                    Drop here
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <AddCandidateDrawer
        open={addOpen}
        onOpenChange={setAddOpen}
        jobId={jobId}
        firstStageId={stages.find((s) => !s.is_won && !s.is_lost)?.id ?? stages[0]?.id ?? null}
        persons={persons}
      />
    </div>
  )
}

// ─────────────────── Applications tab (linear list) ───────────────────

function ApplicationsTab({ jobId, persons }: { jobId: number; persons: PersonLite[] }) {
  const navigate = useNavigate()
  const appsQ = useQuery({
    queryKey: ['ats', 'applications', { job: jobId }],
    queryFn: async () => (await api.get<any[]>(`/ats/applications/?job=${jobId}`)).data,
  })
  const kanbanQ = useQuery({
    queryKey: ['ats', 'kanban', jobId],
    queryFn: async () => (await api.get<KanbanResponse>(`/ats/kanban/?job=${jobId}`)).data,
  })
  const [addOpen, setAddOpen] = useState(false)
  const firstStageId =
    kanbanQ.data?.stages.find((s) => !s.is_won && !s.is_lost)?.id
    ?? kanbanQ.data?.stages[0]?.id ?? null

  if (appsQ.isLoading) return <p className="text-muted-foreground">Loading…</p>
  const apps = appsQ.data || []

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {apps.length === 0
            ? 'No applications yet — add the first candidate.'
            : `${apps.length} application${apps.length === 1 ? '' : 's'} on this job.`}
        </p>
        <Button onClick={() => setAddOpen(true)}><Plus className="h-3 w-3 mr-1" /> Add candidate</Button>
      </div>

      {apps.length > 0 && (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Candidate</Th>
                <Th>Stage</Th>
                <Th>Status</Th>
                <Th>Source</Th>
                <Th>Applied</Th>
                <Th>Resume</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {apps.map((a: any) => (
                <tr
                  key={a.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/ats/applications/${a.id}`)}
                >
                  <Td>
                    <div className="font-medium">{a.person_name}</div>
                    {a.person_email && <div className="text-xs text-muted-foreground">{a.person_email}</div>}
                  </Td>
                  <Td className="text-xs">{a.stage_name}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                      a.status === 'HIRED' ? 'bg-green-100 text-green-800' :
                      a.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                      a.status === 'WITHDRAWN' ? 'bg-gray-100 text-gray-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {a.status}
                    </span>
                  </Td>
                  <Td className="text-xs">{a.source || <Dash />}</Td>
                  <Td className="text-xs">{a.applied_at ? new Date(a.applied_at).toLocaleDateString() : <Dash />}</Td>
                  <Td className="text-xs" onClick={(e) => e.stopPropagation()}>
                    {a.resume_url
                      ? <a href={a.resume_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Open</a>
                      : <Dash />}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddCandidateDrawer
        open={addOpen}
        onOpenChange={setAddOpen}
        jobId={jobId}
        firstStageId={firstStageId}
        persons={persons}
      />
    </div>
  )
}

// ─────────────────── Add Candidate drawer ───────────────────

function AddCandidateDrawer({
  open, onOpenChange, jobId, firstStageId, persons,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  jobId: number
  firstStageId: number | null
  persons: PersonLite[]
}) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [personId, setPersonId] = useState<number | null>(null)
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [email, setEmail] = useState('')
  const [source, setSource] = useState('')
  const [resumeUrl, setResumeUrl] = useState('')

  function reset() {
    setMode('existing'); setPersonId(null)
    setFirst(''); setLast(''); setEmail('')
    setSource(''); setResumeUrl('')
  }

  const createMut = useMutation({
    mutationFn: async () => {
      if (!firstStageId) throw new Error('No initial stage available')
      let pid = personId
      if (mode === 'new') {
        const newPerson = (await api.post('/hr/persons/', {
          first_name: first, last_name: last, personal_email: email,
        })).data
        pid = newPerson.id
      }
      if (!pid) throw new Error('Pick a candidate or fill in name fields')
      return (await api.post('/ats/applications/', {
        person: pid, job: jobId, stage: firstStageId,
        source, resume_url: resumeUrl, status: 'ACTIVE',
      })).data
    },
    onSuccess: () => {
      toast.success('Candidate added')
      qc.invalidateQueries({ queryKey: ['ats', 'kanban', jobId] })
      qc.invalidateQueries({ queryKey: ['ats', 'applications', { job: jobId }] })
      qc.invalidateQueries({ queryKey: ['ats', 'job', jobId] })
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
      reset()
      onOpenChange(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <RecordDrawer
      open={open}
      onOpenChange={(v) => { onOpenChange(v); if (!v) reset() }}
      title="Add candidate"
      description="Drop a candidate into the first pipeline stage. Pick an existing Person or create a new one inline. Interview, scorecard, and offer are added on the Application record page (later phases)."
      onSubmit={() => createMut.mutate()}
      isPending={createMut.isPending}
      submitLabel="Add"
    >
      <FieldGroup>
        <div className="flex gap-2 text-sm">
          <button type="button"
            className={`px-3 py-1 rounded ${mode === 'existing' ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}
            onClick={() => setMode('existing')}>
            Pick existing Person
          </button>
          <button type="button"
            className={`px-3 py-1 rounded ${mode === 'new' ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}
            onClick={() => setMode('new')}>
            Add new Person
          </button>
        </div>

        {mode === 'existing' ? (
          <Field>
            <FieldLabel htmlFor="ac-person">Candidate *</FieldLabel>
            <SearchableSelect
              id="ac-person"
              value={personId ? String(personId) : NONE}
              onChange={(v) => setPersonId(v === NONE ? null : Number(v))}
              placeholder="— pick —"
              options={[
                { value: NONE, label: '— pick —' },
                ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
              ]}
            />
          </Field>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="ac-first">First name *</FieldLabel>
                <Input id="ac-first" required value={first} onChange={(e) => setFirst(e.target.value)} />
              </Field>
              <Field>
                <FieldLabel htmlFor="ac-last">Last name *</FieldLabel>
                <Input id="ac-last" required value={last} onChange={(e) => setLast(e.target.value)} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="ac-email">Personal email</FieldLabel>
              <Input id="ac-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
          </>
        )}

        <Field>
          <FieldLabel htmlFor="ac-source">Source</FieldLabel>
          <Input id="ac-source" value={source} onChange={(e) => setSource(e.target.value)}
            placeholder="LinkedIn, Referral, Direct, Job board…" />
          <FieldDescription>Where the candidate came from (free text for now).</FieldDescription>
        </Field>

        <Field>
          <FieldLabel htmlFor="ac-resume">Resume URL</FieldLabel>
          <Input id="ac-resume" type="url" value={resumeUrl} onChange={(e) => setResumeUrl(e.target.value)}
            placeholder="https://drive.google.com/…" />
        </Field>
      </FieldGroup>
    </RecordDrawer>
  )
}

// ─────────────────── Helpers ───────────────────

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}

// ─────────────────── Pipeline migration drawer ───────────────────

type MigrateStageLite = {
  id: number; name: string; order: number; is_won: boolean; is_lost: boolean
  application_count: number
}

function MigratePipelineDrawer({
  open, onOpenChange, job, pipelines,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  job: Job
  pipelines: PipelineLite[]
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [targetPipelineId, setTargetPipelineId] = useState<number | null>(null)
  const [mapping, setMapping] = useState<Record<number, number | null>>({})

  // Wipe state every time the drawer is opened from scratch.
  useEffect(() => {
    if (open) {
      setTargetPipelineId(null)
      setMapping({})
    }
  }, [open])

  // Current pipeline's stages — gives us the list of "from" stages we need
  // to map. We only need stages that currently hold ≥ 1 application.
  const currentStagesQ = useQuery({
    queryKey: ['ats', 'stages', { pipeline: job.pipeline }],
    queryFn: async () => {
      if (!job.pipeline) return [] as MigrateStageLite[]
      return (await api.get<MigrateStageLite[]>(`/ats/stages/?pipeline=${job.pipeline}`)).data
    },
    enabled: open && !!job.pipeline,
  })

  // Applications grouped per stage for THIS job — drives the "N apps" counts.
  const appsQ = useQuery({
    queryKey: ['ats', 'applications', { job: job.id }],
    queryFn: async () =>
      (await api.get<{ id: number; stage: number; status: string }[]>(
        `/ats/applications/?job=${job.id}`,
      )).data,
    enabled: open,
  })
  const apps = appsQ.data || []
  const countsByStage = new Map<number, number>()
  for (const a of apps) countsByStage.set(a.stage, (countsByStage.get(a.stage) || 0) + 1)

  // Target pipeline stages — the "to" choices.
  const targetStagesQ = useQuery({
    queryKey: ['ats', 'stages', { pipeline: targetPipelineId }],
    queryFn: async () => {
      if (!targetPipelineId) return [] as MigrateStageLite[]
      return (await api.get<MigrateStageLite[]>(
        `/ats/stages/?pipeline=${targetPipelineId}`,
      )).data
    },
    enabled: open && !!targetPipelineId,
  })

  const currentStages = (currentStagesQ.data || []).slice().sort(
    (a, b) => a.order - b.order || a.id - b.id,
  )
  const targetStages = (targetStagesQ.data || []).slice().sort(
    (a, b) => a.order - b.order || a.id - b.id,
  )
  const stagesInUse = currentStages.filter((s) => (countsByStage.get(s.id) || 0) > 0)

  // Pre-fill mapping with a "best guess" — match by stage name (case-insensitive),
  // fall back to the first non-terminal in target. Recomputes whenever the
  // target pipeline OR the loaded stage data changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!targetPipelineId) return
    if (currentStages.length === 0 || targetStages.length === 0) return
    const next: Record<number, number | null> = {}
    for (const src of stagesInUse) {
      const exact = targetStages.find((t) => t.name.toLowerCase() === src.name.toLowerCase())
      const fallback = targetStages.find((t) => !t.is_won && !t.is_lost) ?? targetStages[0]
      next[src.id] = exact?.id ?? fallback?.id ?? null
    }
    setMapping(next)
  }, [targetPipelineId, currentStages.length, targetStages.length])

  function resetAll() {
    setTargetPipelineId(null)
    setMapping({})
  }

  const migrateMut = useMutation({
    mutationFn: async () => {
      if (!targetPipelineId) throw new Error('Pick a target pipeline')
      const unmapped = stagesInUse.filter((s) => !mapping[s.id])
      if (unmapped.length > 0) {
        throw new Error(`Map every stage in use first (${unmapped.map((s) => s.name).join(', ')}).`)
      }
      return (await api.post(`/ats/jobs/${job.id}/migrate-pipeline/`, {
        pipeline: targetPipelineId,
        stage_mapping: mapping,
      })).data
    },
    onSuccess: () => {
      toast.success('Pipeline migrated — applications remapped')
      qc.invalidateQueries({ queryKey: ['ats', 'job', job.id] })
      qc.invalidateQueries({ queryKey: ['ats', 'jobs'] })
      qc.invalidateQueries({ queryKey: ['ats', 'applications', { job: job.id }] })
      qc.invalidateQueries({ queryKey: ['ats', 'kanban', job.id] })
      resetAll()
      onOpenChange(false)
      navigate(`/admin/ats/jobs/${job.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const pipelineOptions = pipelines
    .filter((p) => p.id !== job.pipeline)
    .map((p) => ({
      value: String(p.id),
      label: p.name,
      hint: `${p.stage_count} stage${p.stage_count === 1 ? '' : 's'}${p.is_default ? ' · default' : ''}`,
    }))

  return (
    <RecordDrawer
      open={open}
      onOpenChange={(v) => { if (!v) resetAll(); onOpenChange(v) }}
      title={`Migrate "${job.title}" to a different pipeline`}
      description="Pick a target pipeline, then choose where each currently-used stage should land. Every application is moved atomically; activity logs record the change."
      onSubmit={() => migrateMut.mutate()}
      isPending={migrateMut.isPending}
      submitLabel="Migrate"
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="mig-target">Target pipeline *</FieldLabel>
          <SearchableSelect
            id="mig-target"
            value={targetPipelineId ? String(targetPipelineId) : NONE}
            onChange={(v) => {
              setTargetPipelineId(v === NONE ? null : Number(v))
              setMapping({})
            }}
            placeholder="— pick a pipeline —"
            options={[{ value: NONE, label: '— pick a pipeline —' }, ...pipelineOptions]}
          />
          <FieldDescription>
            Current pipeline: <strong>{job.pipeline_name || '(none)'}</strong>. Same-pipeline migration is rejected.
          </FieldDescription>
        </Field>

        {targetPipelineId && stagesInUse.length > 0 && (
          <Field>
            <FieldLabel>Stage mapping *</FieldLabel>
            <div className="rounded border border-border divide-y divide-border">
              {stagesInUse.map((src) => (
                <div key={src.id} className="px-3 py-2 grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
                  <div>
                    <div className="text-sm font-medium">{src.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {countsByStage.get(src.id)} application
                      {countsByStage.get(src.id) === 1 ? '' : 's'} here
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">→</div>
                  <SearchableSelect
                    value={mapping[src.id] ? String(mapping[src.id]) : NONE}
                    onChange={(v) =>
                      setMapping((m) => ({ ...m, [src.id]: v === NONE ? null : Number(v) }))
                    }
                    placeholder="— pick a target stage —"
                    options={[
                      { value: NONE, label: '— pick a target stage —' },
                      ...targetStages.map((t) => ({
                        value: String(t.id),
                        label: `${t.name}${t.is_won ? ' (Won)' : t.is_lost ? ' (Lost)' : ''}`,
                      })),
                    ]}
                  />
                </div>
              ))}
            </div>
            <FieldDescription>
              We pre-filled by matching stage names. Adjust where the candidate's status should land in the new pipeline.
            </FieldDescription>
          </Field>
        )}

        {targetPipelineId && stagesInUse.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No active applications to remap — submitting will just flip the pipeline.
          </p>
        )}
      </FieldGroup>
    </RecordDrawer>
  )
}

// ─────────────────── Job tags editor ───────────────────

const JOB_TAG_CATEGORY_COLOR: Record<string, string> = {
  SKILL: 'bg-violet-100 text-violet-700',
  SENIORITY: 'bg-purple-100 text-purple-700',
  LOCATION: 'bg-emerald-100 text-emerald-700',
  LANGUAGE: 'bg-cyan-100 text-cyan-700',
  DEPARTMENT: 'bg-orange-100 text-orange-700',
  OTHER: 'bg-gray-100 text-gray-700',
  '': 'bg-gray-100 text-gray-600',
}

function JobTagsEditor({
  allTags, value, onChange,
}: {
  allTags: Tag[]
  value: Tag[]
  onChange: (tagIds: number[]) => void
}) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [input, setInput] = useState('')

  const createMut = useMutation({
    mutationFn: async (name: string) => (await api.post('/ats/tags/', { name })).data as Tag,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['ats', 'tags'] })
      onChange([...value.map((t) => t.id), created.id])
      setInput(''); setAdding(false)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  function commitInput() {
    const raw = input.trim().toLowerCase().replace(/\s+/g, '-')
    if (!raw) return
    const existing = allTags.find((t) => t.name === raw)
    if (existing) {
      if (!value.find((t) => t.id === existing.id)) {
        onChange([...value.map((t) => t.id), existing.id])
      }
      setInput(''); setAdding(false)
      return
    }
    createMut.mutate(raw)
  }

  function removeTag(id: number) {
    onChange(value.filter((t) => t.id !== id).map((t) => t.id))
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-xs text-muted-foreground mr-1">Tags:</span>
      {value.map((t) => (
        <span key={t.id}
          className={`text-xs px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1 ${JOB_TAG_CATEGORY_COLOR[t.category] || JOB_TAG_CATEGORY_COLOR['']}`}>
          {t.name}
          <button onClick={() => removeTag(t.id)} className="hover:opacity-70" aria-label={`Remove ${t.name}`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {adding ? (
        <input
          autoFocus
          className="text-xs px-2 py-0.5 rounded border border-violet-300 outline-none bg-card"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onBlur={() => { if (input.trim()) commitInput(); else setAdding(false) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitInput() }
            if (e.key === 'Escape') { setInput(''); setAdding(false) }
          }}
          placeholder="tag-name"
          list="ats-job-tag-suggestions"
        />
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          className="text-xs px-2 py-0.5 rounded border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40">
          + tag
        </button>
      )}
      <datalist id="ats-job-tag-suggestions">
        {allTags.map((t) => <option key={t.id} value={t.name} />)}
      </datalist>
    </div>
  )
}
